import { useMemo } from 'react';
import useSlingApi from '@sdk/api/useSlingApi';
import { useInfiniteQuery } from '@tanstack/react-query';
import getErrorForToast from '@sdk/api/utils/getErrorForToast';
import useToast from '@sdk/common/toast/useToast';

import { getArticleCommentsQueryKey } from '@web/newsfeed/utils/getQueryKey';
import { useNewsfeedMappers } from '@web/newsfeed/hooks/useNewsfeedMappers';
import { Article } from '@web/newsfeed/types/Article';

const LOADING_ITEMS_LIMIT = 5;

type Props = {
  article: Article;
};

export const useArticleComments = ({ article }: Props) => {
  const { data, fetchPreviousPage, hasPreviousPage, isFetchingPreviousPage, isLoading } =
    useArticleCommentsData({ article });
  const comments = useMemo(() => data?.pages.flat(), [data]);
  const isLoadingResults = isLoading || isFetchingPreviousPage;

  let loadersCount = 0;
  if (isLoading) {
    loadersCount = isFetchingPreviousPage
      ? 1
      : Math.min(article.commentsCount || 1, LOADING_ITEMS_LIMIT);
  }

  return {
    fetchPreviousPage,
    hasPreviousPage,
    isLoadingResults,
    comments,
    loadersCount,
  };
};

export const useArticleCommentsData = ({ article }: Props) => {
  const { newsfeedComments } = useSlingApi();
  const { showErrorToast } = useToast();
  const queryKey = getArticleCommentsQueryKey(article.id);
  const { isReady, mapAPIArticleCommentToUI } = useNewsfeedMappers();

  return useInfiniteQuery({
    queryKey,
    queryFn: async ({ pageParam }) => {
      const { data } = await newsfeedComments.get(article.page.id, article.id, pageParam?.beforeId);

      return data.map((comment) => mapAPIArticleCommentToUI(comment));
    },

    onError: (error) => {
      showErrorToast(getErrorForToast(error));
    },
    enabled: isReady,
    refetchOnMount: false,
    getPreviousPageParam: (lastPage, allPages) => {
      const [oldestComment] = lastPage;
      const loadedCommentsCount = allPages.reduce((result, page) => result + page.length, 0);
      if (loadedCommentsCount >= article.commentsCount) {
        return;
      }

      return { beforeId: oldestComment.id };
    },
  });
};
